import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import {
  Box, VStack, HStack, FormControl, FormLabel, Input, InputGroup, InputRightElement,
  IconButton, Button, Text, Link, Alert, AlertIcon, Heading, Image
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { authState } from '../../state/authState';
import { authService } from '../../services/api';
import { validatePassword } from '../../utils/passwordValidation';

export const RegisterForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const setAuth = useSetRecoilState(authState);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.register({
        email: formData.email,
        username: formData.username,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      setAuth({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false
      });
      navigate('/calculator');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      maxW="450px"
      mx="auto"
      mt={12}
      p={8}
      bg="rgba(255, 255, 255, 0.95)"
      borderRadius="lg"
      boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5)"
      backdropFilter="blur(10px)"
      border="1px solid"
      borderColor="gray.200"
    >
      <VStack spacing={5} as="form" onSubmit={handleSubmit}>
        <Image src="/img/iadc-logo.png" alt="IADC" h="50px" />
        <Heading size="lg" color="brand.500">Create Account</Heading>
        <Text color="gray.600" textAlign="center">Geothermal Well Complexity Index</Text>

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <HStack spacing={4} w="100%">
          <FormControl isRequired>
            <FormLabel>First Name</FormLabel>
            <Input
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Last Name</FormLabel>
            <Input
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
            />
          </FormControl>
        </HStack>

        <FormControl isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Username</FormLabel>
          <Input
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="johndoe123"
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Password</FormLabel>
          <InputGroup>
            <Input
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              placeholder="Min. 8 chars, upper + lower + number"
            />
            <InputRightElement>
              <IconButton
                icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                onClick={() => setShowPassword(prev => !prev)}
                variant="ghost"
                size="sm"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              />
            </InputRightElement>
          </InputGroup>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Confirm Password</FormLabel>
          <InputGroup>
            <Input
              name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm password"
            />
            <InputRightElement>
              <IconButton
                icon={showConfirm ? <ViewOffIcon /> : <ViewIcon />}
                onClick={() => setShowConfirm(prev => !prev)}
                variant="ghost"
                size="sm"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              />
            </InputRightElement>
          </InputGroup>
        </FormControl>

        <Button
          type="submit"
          colorScheme="red"
          width="100%"
          isLoading={isLoading}
        >
          Create Account
        </Button>

        <Text fontSize="sm" color="gray.600">
          Already have an account?{' '}
          <Link as={RouterLink} to="/login" color="brand.500" fontWeight="semibold">
            Sign In
          </Link>
        </Text>
      </VStack>
    </Box>
  );
};

export default RegisterForm;
